import { publishEvent } from 'serviceWorker/broadcasts/publishers';
import { TOO_MANY_REQUESTS, UNAUTHORIZED, VERSION_MISMATCH } from 'shared/contants';
import { apiVersionContract } from 'shared/domain/apiVersion/apiVersionContract';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import { LogLevel } from 'shared/types/logger';
import {
  requestSuspender,
  REQUEST_SUSPENDER_TIMEOUT_MS,
} from 'serviceWorker/api/RequestSuspender';
import {
  parseRetryAfterSeconds,
  rateLimitManager,
} from 'serviceWorker/api/RateLimitManager';
import { debugLog } from 'shared/logger/debugLog';
import { swSignals } from 'serviceWorker/events/swSignals';
import { swLog } from 'serviceWorker/helpers/makeSwLogger';
import {
  RateLimitError,
  SessionTokenMissingError,
  UnauthorizedError,
} from 'serviceWorker/helpers/httpError';
import { getPullDiagnostics } from 'serviceWorker/helpers/pullDiagnostics';
import {
  createTimeoutSignal,
  mergeAbortSignals,
} from 'serviceWorker/helpers/abortSignals';
import { hasAuthorizationHeader } from 'serviceWorker/helpers/requestHeaders';

const MAX_RATE_LIMIT_RETRIES = 5;
/**
 * The HTTP queue runs one request at a time, so a request that never settles
 * would stall it (and every response the page waits for) until the SW restarts.
 */
export const PULL_TIMEOUT_MS = 120_000;

export type PullOptions = Readonly<{
  /**
   * Whether the user's session token authorizes the request (default): it is
   * never sent without it, and a 401 ends the session. False for public
   * requests authorized by a token in the URL, where a 401 is returned to the
   * caller and says nothing about the user's session.
   */
  authorizedBySession?: boolean;
}>;

function resolveUrlPath(urlRequestInfo: RequestInfo): string {
  const url =
    typeof urlRequestInfo === 'string'
      ? urlRequestInfo
      : urlRequestInfo?.url || urlRequestInfo?.toString?.() || 'unknown';

  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

/**
 * A session request prepared before the session ended carries no token: it is
 * not sent, since its 401 would sign the user out again (PT-7259).
 */
function refuseSessionRequestWithoutToken(urlPath: string, method: string, headers?: HeadersInit): void {
  if (hasAuthorizationHeader(headers)) {
    return;
  }
  swLog('Request not sent: no session token', LogLevel.INFO, null, {
    query: { url: urlPath, method },
    setup: getPullDiagnostics(),
  });
  throw new SessionTokenMissingError(urlPath, method);
}

export async function pull(
  urlRequestInfo: RequestInfo,
  init?: RequestInit,
  { authorizedBySession = true }: PullOptions = {}
): Promise<Response> {
  const urlPath = resolveUrlPath(urlRequestInfo);
  const method = init?.method || 'GET';
  if (authorizedBySession) {
    refuseSessionRequestWithoutToken(urlPath, method, init?.headers);
  }
  let rateLimitAttempts = 0;

  while (true) {
    await rateLimitManager.waitUntilClear();

    const slot = await requestSuspender.registerWhenReady(
      REQUEST_SUSPENDER_TIMEOUT_MS
    );
    if (!slot) {
      throw new Error('Operations suspended - request blocked (timeout)');
    }

    const { id, signal: suspenderSignal } = slot;

    try {
      const timeout = createTimeoutSignal(PULL_TIMEOUT_MS);
      const requestSignals = init?.signal
        ? [init.signal, suspenderSignal, timeout.signal]
        : [suspenderSignal, timeout.signal];
      const { signal, dispose } = mergeAbortSignals(...requestSignals);

      try {
        const response = await fetch(urlRequestInfo, {
          ...init,
          signal,
        });

        if (response.status === TOO_MANY_REQUESTS) {
          const retryAfter = parseRetryAfterSeconds(
            response.headers.get('Retry-After')
          );
          const { remainingMs } = rateLimitManager.activate(retryAfter);

          if (rateLimitManager.shouldLog()) {
            swLog(
              'sw-pull-429',
              LogLevel.INFO,
              new Error(`HTTP 429 on ${urlPath}`),
              {
                query: {
                  url: urlPath,
                  method,
                  status: response.status,
                  retryAfterSeconds: retryAfter ?? null,
                  remainingMs,
                  attempt: rateLimitAttempts + 1,
                },
              }
            );
          }

          rateLimitAttempts += 1;
          if (rateLimitAttempts >= MAX_RATE_LIMIT_RETRIES) {
            throw new RateLimitError(
              `Rate limited. Path: ${urlPath}`,
              urlPath,
              method,
              retryAfter,
              remainingMs
            );
          }

          continue;
        }

        if (response.status === UNAUTHORIZED && authorizedBySession) {
          debugLog('SW pull: received 401, triggering logout', { url: urlPath });

          const error = new UnauthorizedError(
            `Unauthorized. Path: ${urlPath}`,
            urlPath,
            {
              method,
              statusText: response.statusText,
              hasAuthHeader: hasAuthorizationHeader(init?.headers),
            }
          );

          swLog(error.message, LogLevel.ERROR, error, {
            query: {
              url: urlPath,
              method,
              status: response.status,
              statusText: response.statusText,
              hasAuthHeader: error.details?.hasAuthHeader ?? false,
            },
          });

          swSignals.unauthorizedResponse.emit({ url: urlPath, method });
          throw error;
        }

        if (response.status === VERSION_MISMATCH) {
          // The caller still gets the failure; the pages show that the app must be reloaded.
          publishEvent(apiVersionContract, DomainMessagesTypes.apiVersionMismatch, undefined);
        }

        return response;
      } finally {
        dispose();
        timeout.dispose();
      }
    } finally {
      requestSuspender.unregister(id);
    }
  }
}
